<script setup lang="ts">
import { computed } from 'vue'
import { useFormStore } from '@/stores/form'
import { getAdvertiser } from '@/config/advertisers'

const formStore = useFormStore()

const advertiser = computed(() => {
  const id = formStore.data.advertiserId
  return id ? getAdvertiser(id) : null
})

const buyerName = computed(() => advertiser.value?.buyer || 'a debt specialist')
const phoneNumber = computed(() => advertiser.value?.did)
</script>

<template>
  <div class="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
    <!-- Success icon -->
    <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-fade-in">
      <svg class="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
    </div>

    <!-- Heading -->
    <h1 class="text-2xl font-bold text-gray-800 mb-3 animate-slide-up">
      Thank You{{ formStore.data.firstName ? `, ${formStore.data.firstName}` : '' }}!
    </h1>

    <!-- Message -->
    <p class="text-gray-600 mb-6 animate-slide-up max-w-sm">
      Your information has been submitted successfully.
      <span v-if="advertiser">A specialist from {{ buyerName }}</span>
      <span v-else>A debt specialist</span>
      will contact you shortly to discuss your options.
    </p>

    <!-- Call to action when phone number available -->
    <div v-if="phoneNumber" class="bg-green-50 rounded-2xl p-5 w-full max-w-sm mb-4 animate-slide-up">
      <p class="text-gray-700 mb-3">For priority support, call now:</p>
      <a
        :href="`tel:${phoneNumber}`"
        class="block w-full py-4 px-6 bg-green-500 hover:bg-green-600 text-white font-bold text-xl rounded-xl text-center transition-colors"
      >
        {{ phoneNumber }}
      </a>
      <p class="text-xs text-gray-500 mt-2 text-center">Tap to call {{ buyerName }}</p>
    </div>

    <!-- What's next -->
    <div class="bg-blue-50 rounded-2xl p-5 w-full max-w-sm text-left animate-slide-up">
      <h3 class="font-semibold text-gray-800 mb-3">What happens next?</h3>
      <ul class="space-y-2 text-sm text-gray-600">
        <li class="flex items-start gap-2">
          <span class="text-primary font-bold">1.</span>
          <span>A specialist from {{ buyerName }} will review your information</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="text-primary font-bold">2.</span>
          <span>You'll receive a call within 24 hours</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="text-primary font-bold">3.</span>
          <span>Get your personalized debt relief plan</span>
        </li>
      </ul>
    </div>
  </div>
</template>
